/**
 * TopHeader — Iter175 (mobile-responsive rewrite).
 *
 * The old implementation crammed logo + back link + title + Claire help +
 * theme toggle + lang toggle + "Add Virtual Business" all into a single
 * flex row, using `px-8` and no responsive breakpoints. On phones this
 * produced the "embaralhado" bug reported on production (agentelead-pro.com):
 * every label wrapped and stacked on top of the H1.
 *
 * The rewrite mirrors the AppHeader kebab pattern:
 *   • <md : logo + truncated title + a "+" primary CTA + kebab menu with
 *           the low-frequency toggles (Claire help, theme, language, back).
 *   • ≥md : desktop layout intact — everything visible inline.
 */
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, Plus, MoreVertical, Menu } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { useSidebarState } from "./SidebarContext";
import LangToggle from "./LangToggle";
import ThemeToggle from "./ThemeToggle";
import ClaireHelpButton from "./ClaireHelpButton";
import Logo from "./Logo";

export default function TopHeader({ onAddManual }) {
  const { t } = useLang();
  const { setMobileOpen } = useSidebarState();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    window.addEventListener("touchstart", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("touchstart", onClick);
    };
  }, [menuOpen]);

  return (
    <header
      data-testid="top-header"
      className="sticky top-0 z-30 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-3 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-2"
    >
      {/* LEFT — logo + title (always visible; truncates on mobile) */}
      <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label={t("header.open_nav")}
          data-testid="top-header-sidebar-toggle"
          className="lg:hidden w-9 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] flex items-center justify-center text-[var(--al-text)] hover:border-[#C9A24E]"
        >
          <Menu className="w-4 h-4" />
        </button>
        <Logo testid="header-logo-icon" className="w-7 h-7 sm:w-8 sm:h-8 object-contain shrink-0" />
        {/* Back link — hidden on mobile (accessible via kebab) */}
        <button
          data-testid="back-to-prospect-accounts"
          className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-[var(--al-text-muted)] hover:text-[#3B82F6] transition-colors shrink-0"
        >
          <ChevronLeft className="w-4 h-4" />
          {t("header.back")}
        </button>
        <div className="hidden md:block h-5 w-px bg-[var(--al-border)]" />
        <h1 className="text-[14px] sm:text-[16px] font-semibold text-[var(--al-text)] truncate">
          {t("header.title")}
        </h1>
      </div>

      {/* DESKTOP toolbar — full inline layout */}
      <div className="hidden md:flex items-center gap-2 shrink-0">
        <ClaireHelpButton testid="prospecting"
          question="Como uso a Lead Discovery — buscar leads locais, rodar auditorias e enviar para o CRM?" />
        <ThemeToggle />
        <LangToggle />
        <button
          data-testid="add-virtual-business-btn"
          onClick={onAddManual}
          className="al-btn-primary flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium"
        >
          <Plus className="w-4 h-4" />
          {t("header.add_manual")}
        </button>
      </div>

      {/* MOBILE toolbar — primary CTA + kebab dropdown */}
      <div className="md:hidden flex items-center gap-1.5 shrink-0 relative" ref={menuRef}>
        <button
          data-testid="add-virtual-business-btn-mobile"
          onClick={onAddManual}
          aria-label={t("header.add_manual")}
          className="al-btn-primary flex items-center justify-center w-9 h-9 rounded-lg"
        >
          <Plus className="w-4 h-4" />
        </button>
        <button
          type="button"
          data-testid="top-header-kebab"
          aria-expanded={menuOpen}
          aria-label={t("header.open_menu")}
          onClick={() => setMenuOpen((v) => !v)}
          className="w-9 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] inline-flex items-center justify-center hover:border-[#C9A24E] transition-colors"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {menuOpen && (
          <div
            data-testid="top-header-mobile-menu"
            className="absolute right-0 top-11 z-50 w-56 max-w-[calc(100vw-24px)] rounded-xl border border-[var(--al-border)] bg-[var(--al-card-bg)] shadow-2xl p-2 space-y-1"
            onClick={() => setMenuOpen(false)}
          >
            <div className="px-2 py-1.5 flex items-center justify-between gap-2">
              <span className="text-[11.5px] text-[var(--al-text-muted)] uppercase tracking-widest font-bold">{t("header.theme")}</span>
              <ThemeToggle />
            </div>
            <div className="px-2 py-1.5 flex items-center justify-between gap-2">
              <span className="text-[11.5px] text-[var(--al-text-muted)] uppercase tracking-widest font-bold">{t("header.language")}</span>
              <LangToggle />
            </div>
            <div className="px-2 py-1.5 flex items-center justify-between gap-2">
              <span className="text-[11.5px] text-[var(--al-text-muted)] uppercase tracking-widest font-bold">{t("header.help")}</span>
              <ClaireHelpButton testid="prospecting"
                question="Como uso a Lead Discovery — buscar leads locais, rodar auditorias e enviar para o CRM?" />
            </div>
            <button
              data-testid="back-to-prospect-accounts-mobile"
              className="w-full flex items-center gap-2 px-3 h-10 rounded-md text-[13px] text-[var(--al-text)] hover:bg-white/5"
            >
              <ChevronLeft className="w-4 h-4" />
              {t("header.back")}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
